import { useState, useEffect } from 'react';
import type { Device, Employee } from '../types';
import { api } from '../utils/api';
import SearchableSelect from './SearchableSelect';

interface DeviceModalProps {
  isOpen: boolean;
  device?: Device;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DeviceModal({ isOpen, device, onClose, onSuccess }: DeviceModalProps) {
  const [formData, setFormData] = useState({
    idEmployee: '',
    name: '',
    mac: '',
    status: 'published'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableEmployees, setAvailableEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  useEffect(() => {
    if (device) {
      setFormData({
        idEmployee: device.idEmployee || '',
        name: device.name || '',
        mac: device.mac || '',
        status: device.status || 'published'
      });
    } else {
      setFormData({
        idEmployee: '',
        name: '',
        mac: '',
        status: 'published'
      });
    }
    setError('');
  }, [device, isOpen]);

  // Fetch available employees (those without devices)
  useEffect(() => {
    const fetchAvailableEmployees = async () => {
      if (!isOpen) return;
      
      setLoadingEmployees(true);
      try {
        // Get all employees
        const { employees } = await api.getAllEmployees({ limit: 1000 });
        
        // Get all devices to check which employees already have devices
        const devices = await api.getAllDevices();
        const employeesWithDevices = new Set(
          devices
            .filter(d => d.idEmployee)
            .map(d => d.idEmployee)
        );
        
        // Filter employees without devices, or include current device's employee if editing
        const available = employees.filter(emp => 
          !employeesWithDevices.has(emp.idEmployee) || 
          (device && emp.idEmployee === device.idEmployee)
        );
        
        setAvailableEmployees(available);
      } catch (err) {
        console.error('Failed to fetch employees:', err);
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchAvailableEmployees();
  }, [isOpen, device]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Convert employees to select options
  const employeeOptions = availableEmployees.map(emp => ({
    value: emp.idEmployee,
    label: `${emp.NIP || emp.idEmployee} - ${emp.Nama} ${emp.Jabatan ? `(${emp.Jabatan})` : ''}`
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (device) {
        // Update device - only send editable fields
        await api.updateDevice(device.id, {
          idEmployee: formData.idEmployee,
          name: formData.name,
          status: formData.status
        });
      } else {
        // Create device
        await api.createDevice({
          idEmployee: formData.idEmployee,
          name: formData.name,
          mac: formData.mac,
          status: formData.status
        });
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save device');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold">
            {device ? 'Edit Device' : 'Add Device'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign to Employee
            </label>
            <SearchableSelect
              options={employeeOptions}
              value={formData.idEmployee}
              onChange={(value) => setFormData(prev => ({ ...prev, idEmployee: value }))}
              placeholder="-- Select Employee or Leave Empty to Unassign --"
              searchPlaceholder="Search by NIP, name, or position..."
              isLoading={loadingEmployees}
              emptyMessage={availableEmployees.length === 0 ? "No available employees without devices" : "No employees found"}
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty to unassign device from employee</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Device Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Device 001"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {!device && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MAC Address *
              </label>
              <input
                type="text"
                name="mac"
                value={formData.mac}
                onChange={handleChange}
                placeholder="e.g., 00:11:22:33:44:55"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Format: XX:XX:XX:XX:XX:XX</p>
            </div>
          )}

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="published">Active</option>
              <option value="draft">Inactive</option>
              <option value="offline">Offline</option>
            </select>
          </div> */}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : device ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
